import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
} from "react";

import Prompt from "src/components/messages/Prompt";

import { UnifizeChat } from "src/types";
import { Nullable } from "src/ts-types";

import { groupRecentChats } from "./utils";
import { RecentChats as StyledRecentChats } from "./styles";
import CollapsibleChat from "./CollapsibleChat";

interface Props {
  historyRef: React.RefObject<Nullable<HTMLDivElement>>;
  roomId: string;
  chats: Array<UnifizeChat>;
  renderMessageMarker: () => React.ReactNode;
  lastMessage: Nullable<string>;
  messagesEnd: React.RefObject<HTMLDivElement>;
}

const RecentChats = ({
  historyRef,
  roomId,
  chats,
  renderMessageMarker,
  lastMessage,
  messagesEnd,
}: Props) => {
  const chatRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const pendingScrollRestore = useRef<{
    e: Nullable<HTMLElement>;
    prevTop: number;
  }>({ e: null, prevTop: 0 });
  const [notificationsExpanded, setNotificationsExpanded] = useState(false);
  const [stickyTop, setStickyTop] = useState(false);
  const groupedChats = useMemo(
    () => groupRecentChats(chats, notificationsExpanded),
    [chats, notificationsExpanded],
  );

  const handleToggle = (e: Nullable<HTMLDivElement>) => {
    if (!e) return;
    setNotificationsExpanded((prev) => {
      const prevTop = e.getBoundingClientRect().top;
      pendingScrollRestore.current = { e, prevTop };
      return !prev;
    });
  };

  useLayoutEffect(() => {
    if (!pendingScrollRestore.current) return;
    if (historyRef.current && pendingScrollRestore.current.e) {
      const currentTop =
        pendingScrollRestore.current.e.getBoundingClientRect().top;
      const delta = currentTop - pendingScrollRestore.current.prevTop;
      historyRef.current.scrollTop += delta;
    }
    pendingScrollRestore.current.e = null;
  }, [notificationsExpanded]);

  useEffect(() => {
    setNotificationsExpanded(false);
  }, [roomId]);

  useEffect(() => {
    setStickyTop(notificationsExpanded);
  }, [notificationsExpanded]);

  useEffect(() => {
    const root = historyRef?.current;
    if (!root || !notificationsExpanded) return;

    const visibleTopOffsetByIndex = new Map<string, number>(); // chat-index -> distance-to-top
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const chatIndex =
            (entry.target as HTMLElement).dataset["index"] ?? "";
          const rootTop = entry.rootBounds?.top ?? 0;
          const distToTop = entry.boundingClientRect.top - rootTop;

          if (entry.isIntersecting) {
            visibleTopOffsetByIndex.set(chatIndex, distToTop);
          } else {
            visibleTopOffsetByIndex.delete(chatIndex);
          }
        }

        // Pick the visible chat whose top is closest to the chatroom's top
        let bestIndex: Nullable<string> = null;
        let best = Infinity;
        for (const [index, distance] of visibleTopOffsetByIndex) {
          const score = Math.abs(distance);
          if (score < best) {
            best = score;
            bestIndex = index;
          }
        }

        if (notificationsExpanded && bestIndex !== null) {
          const chat = groupedChats[0][Number(bestIndex)];
          setStickyTop(!(chat && chat.collapsedState === "none"));
        }
      },
      { root, threshold: [0] },
    );

    // Observe all the chats
    for (const m of groupedChats[0]) {
      const el = chatRefs.current.get(m.id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [groupedChats, notificationsExpanded]);

  return (
    <StyledRecentChats>
      {groupedChats[0].map(
        (chat, index) =>
          chat && (
            <CollapsibleChat
              key={chat.id}
              sticky={stickyTop}
              collapsibleChat={chat}
              notificationsExpanded={notificationsExpanded}
              toggleExpansion={handleToggle}
              lastMessage={lastMessage}
              renderMessageMarker={renderMessageMarker}
              roomId={roomId}
              firstMessage={true}
              stackCount={groupedChats[1][index] ?? 0}
              dataIndex={index}
              ref={(el) => {
                if (el) {
                  chatRefs.current.set(chat.id, el);
                } else {
                  chatRefs.current.delete(chat.id);
                }
              }}
            />
          ),
      )}
      <Prompt key="prompt" />
      <div
        key="bottom"
        style={{ float: "left", clear: "both" }}
        ref={messagesEnd}
      />
    </StyledRecentChats>
  );
};

export default RecentChats;
